import type { ChatItem, ChatRequest, ChatResponse, ConversationSummary } from "../types";
import { getAccessToken, getClientPlatform, getMailboxContext } from "../office/office";

// Typed client for the backend. Attaches the Entra SSO bearer token when
// available (phase 2); phase 1 runs unauthenticated.

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Attach the analytics-only turn context.
 *
 *  Added here, at the single outbound seam, rather than at each call site — a
 *  second call site that forgot it would silently produce turns labelled
 *  "unreported" and a chart that quietly understates shared-mailbox usage.
 *  Neither value affects the answer; the backend normalizes both to a closed set. */
function withTurnContext<T extends object>(req: T): T & { mailboxContext: string | null; clientPlatform: string | null } {
  return { ...req, mailboxContext: getMailboxContext(), clientPlatform: getClientPlatform() };
}

/** Send a turn, optionally rendering the assistant's lead-in as soon as it arrives.
 *
 *  With `onReply`, the request asks for server-sent events and the backend emits the
 *  `reply` field the moment it closes in the model's JSON — typically seconds before the
 *  card. The turn takes exactly as long either way; it just stops being a blank spinner.
 *
 *  The FINAL object always comes from the `done` event, never assembled on the client, so
 *  a streamed turn renders identically to a non-streamed one. Any stream failure falls
 *  back to a normal POST rather than surfacing an error: the answer matters more than the
 *  progressive display. */
export async function sendChat(
  req: ChatRequest & { conversationId?: string | null },
  onReply?: (text: string) => void,
): Promise<ChatResponse> {
  if (onReply) {
    try {
      return await streamChat(req, onReply);
    } catch (err) {
      // A NotAuthenticatedYet must NOT be retried as a plain POST — it would just fail
      // again and the second failure would mask the real cause.
      if (err instanceof NotAuthenticatedYet) throw err;
      console.warn("[chat] streaming failed, falling back to a plain request:", err);
    }
  }
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(withTurnContext(req)),
  });
  // 401/403 here means the SSO token was missing or rejected, which is a
  // completely different thing to tell the user than "the backend broke" — it was
  // the reported symptom "the assistant is not responding" for a user who simply
  // isn't assigned to the add-in's Entra app.
  //
  // Deliberately NOT classify(): on this route 503 is `auth_not_configured`, not
  // "no history store", so borrowing that mapping would hide the history UI over
  // an unrelated backend misconfiguration.
  if (res.status === 401 || res.status === 403) throw new NotAuthenticatedYet(`chat auth failed (${res.status})`);
  if (!res.ok) throw new Error(`chat failed: ${res.status}`);
  return (await res.json()) as ChatResponse;
}

/** SSE transport for sendChat. Kept separate so the plain path stays exactly as it was. */
async function streamChat(
  req: ChatRequest & { conversationId?: string | null },
  onReply: (text: string) => void,
): Promise<ChatResponse> {
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { ...(await authHeaders()), Accept: "text/event-stream" },
    body: JSON.stringify(withTurnContext(req)),
  });
  if (res.status === 401 || res.status === 403) throw new NotAuthenticatedYet(`chat auth failed (${res.status})`);
  if (!res.ok) throw new Error(`chat failed: ${res.status}`);
  if (!res.body) throw new Error("chat stream had no body");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: ChatResponse | null = null;

  for (;;) {
    const { value, done: finished } = await reader.read();
    if (value) buf += decoder.decode(value, { stream: true });
    // Events are separated by a blank line; a partial event stays in the buffer.
    let sep: number;
    while ((sep = buf.indexOf("\n\n")) !== -1) {
      const raw = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = "message";
      let data = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!data) continue;
      const parsed = JSON.parse(data) as Record<string, unknown>;
      if (event === "reply" && typeof parsed.text === "string") onReply(parsed.text);
      else if (event === "done") done = parsed as unknown as ChatResponse;
      else if (event === "error") throw new Error(`chat failed: ${String(parsed.error ?? "stream error")}`);
    }
    if (finished) break;
  }
  // A stream that ends without `done` is a truncated turn, not an empty answer — say so
  // rather than rendering a blank card.
  if (!done) throw new Error("chat stream ended before the response arrived");
  return done;
}

/** Thrown when the backend has no conversation store configured.
 *  Distinct from a real failure so the pane can hide the history UI entirely
 *  rather than showing an empty list, which would read as "you have no chats". */
export class HistoryUnavailable extends Error {}

/** Thrown when the request went out without a usable SSO token.
 *
 *  Its own type because it means "ask again shortly", not "this doesn't work here".
 *  Office.js SSO isn't available the instant the pane mounts, so an early probe
 *  goes out unauthenticated and comes back 401 — which previously looked identical
 *  to a hard failure and left the history UI hidden until something else turned it
 *  on. See the retry in App.tsx. */
export class NotAuthenticatedYet extends Error {}

function classify(status: number): Error | null {
  if (status === 503) return new HistoryUnavailable("history_unavailable");
  if (status === 401 || status === 403) return new NotAuthenticatedYet(`auth not ready (${status})`);
  return null;
}

export async function listConversations(): Promise<ConversationSummary[]> {
  const res = await fetch("/api/conversations", { headers: await authHeaders() });
  const known = classify(res.status);
  if (known) throw known;
  if (!res.ok) throw new Error(`conversations failed: ${res.status}`);
  return ((await res.json()) as { conversations: ConversationSummary[] }).conversations;
}

export async function loadConversation(
  id: string,
): Promise<{ summary: ConversationSummary; messages: { seq: number; role: string; body: ChatItem | null }[] }> {
  const res = await fetch(`/api/conversations/${encodeURIComponent(id)}`, { headers: await authHeaders() });
  const known = classify(res.status);
  if (known) throw known;
  if (!res.ok) throw new Error(`conversation failed: ${res.status}`);
  return await res.json();
}

/** Why a "Draft reply from HR mailbox" click didn't produce a draft. Every arm maps
 *  to a different thing the user can do about it, which is the whole point of not
 *  collapsing them into one "failed" toast. */
export type HrDraftFailure = "unauthorized" | "mailbox_disabled" | "mailbox_busy" | "not_in_shared_mailbox" | "bad_request" | "failed";
/** `draftId` is the Graph id of the reply created INSIDE the shared mailbox. The pane
 *  uses it to open that draft in Outlook's own form, which is the only way to get a
 *  native editor showing the HR From address -- the item already carries it.
 *  `webLink` is kept because Graph returns it and it costs nothing to pass on, but the
 *  pane deliberately does NOT open it: a browser window takes the user out of Outlook. */
export type HrDraftResult =
  | { ok: true; draftId: string | null; ewsId: string | null; webLink: string | null; signatureImagesFailed: number }
  | { ok: false; reason: HrDraftFailure; detail?: string };

/** Create a reply DRAFT inside the HR shared mailbox (Graph, app-only).
 *
 *  This replaced sendFromSharedMailbox on 2026-08-10, and the difference is the
 *  point: nothing in this app sends mail any more. The draft is created in the
 *  shared mailbox — which is what makes the From address the HR team's without
 *  anyone filling it in — and the human reviews and sends it from Outlook's own
 *  compose window. The backend's one-call send route was removed at the same time,
 *  so review-before-send is a property of the API surface, not a UI convention.
 *
 *  `webLink` opens the draft in OWA, retargeted server-side so the DELEGATE can
 *  open it. Null means Graph gave no link — the draft still exists; point the user
 *  at the HR mailbox's Drafts folder.
 *
 *  Kept in client.ts next to the other authenticated calls: this call's
 *  predecessor shipped as a bare fetch in App.tsx with no Authorization header and
 *  failed 100% of the time in any deployed environment. */
export async function createHrReplyDraft(body: {
  messageId?: string | null;
  internetMessageId?: string | null;
  text: string;
}): Promise<HrDraftResult> {
  let res: Response;
  try {
    res = await fetch("/api/mail/reply-draft", {
      method: "POST",
      headers: await authHeaders(),
      // `replyAll` is sent explicitly rather than left to a server default, because
      // an omitted field reverting to reply-to-sender is invisible from here — the
      // draft still opens, addressed to one person, and nobody notices until a
      // recipient asks why they were dropped. The personal path picks
      // displayReplyAllForm for the same reason (office/office.ts).
      body: JSON.stringify({ ...body, replyAll: true }),
    });
  } catch {
    return { ok: false, reason: "failed" };
  }
  if (res.ok) {
    const parsed = (await res.json().catch(() => null)) as {
      draftId?: string | null;
      ewsId?: string | null;
      webLink?: string | null;
      signatureImagesFailed?: number;
    } | null;
    return {
      ok: true,
      draftId: parsed?.draftId ?? null,
      ewsId: parsed?.ewsId ?? null,
      webLink: parsed?.webLink ?? null,
      signatureImagesFailed: parsed?.signatureImagesFailed ?? 0,
    };
  }
  if (res.status === 401 || res.status === 403) return { ok: false, reason: "unauthorized" };
  if (res.status === 503) {
    // Two very different things share this status on /api/mail: the feature flag
    // being off, and Exchange being transiently unavailable mid mailbox-move. The
    // error CODE separates them — one is "waiting on an admin", the other is "try
    // again in a few minutes", and telling a user the wrong one wastes their time.
    const parsed = (await res.json().catch(() => null)) as { error?: string; detail?: string } | null;
    if (parsed?.error === "mailbox_busy") return { ok: false, reason: "mailbox_busy", detail: parsed.detail };
    return { ok: false, reason: "mailbox_disabled" };
  }
  if (res.status === 400) return { ok: false, reason: "bad_request" };
  if (res.status === 409) {
    const parsed = (await res.json().catch(() => null)) as { detail?: string } | null;
    return { ok: false, reason: "not_in_shared_mailbox", detail: parsed?.detail };
  }
  return { ok: false, reason: "failed" };
}

/** Soft delete — hides the conversation. The analytics row is kept by design, so
 *  don't describe this to the user as erasing everything. */
export async function deleteConversation(id: string): Promise<void> {
  const res = await fetch(`/api/conversations/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`delete failed: ${res.status}`);
}

/** Record a thumbs rating on one turn.
 *
 *  Deliberately FIRE-AND-FORGET at the UI layer: the pane flips the thumb
 *  optimistically and this reports success only so a failure can be logged. A
 *  rating that fails to save is not worth interrupting the user for — but silently
 *  swallowing it would hide a broken endpoint forever, so it is logged.
 *
 *  `feedback: null` clears a rating, which is what makes a mis-click undoable. */
/** `comment` OMITTED and `comment: null` mean different things and both are used: the
 *  thumb is written the instant it is clicked, and the comment only if the user then
 *  types one. Omitting it leaves any stored comment alone; null clears it. Sending
 *  `comment: undefined` would serialise away to the same thing as omitting, which is
 *  why the key is added conditionally rather than always. */
export async function sendFeedback(usageEventId: number, feedback: "up" | "down" | null, comment?: string | null): Promise<boolean> {
  try {
    const payload: Record<string, unknown> = { usageEventId, feedback };
    if (comment !== undefined) payload.comment = comment;
    const res = await fetch("/api/feedback", {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) console.warn(`[feedback] not saved (${res.status})`);
    return res.ok;
  } catch (err) {
    console.warn("[feedback] not saved:", err);
    return false;
  }
}

/** Report where the shared-mailbox draft open succeeded or failed.
 *
 *  Fire-and-forget and silent by design: this is a diagnostic about a diagnostic, and
 *  a failure to report a failure must never become a second thing the user sees. The
 *  draft itself is unaffected either way.
 *
 *  Carries no ids and no message content — see the route. */
export async function reportComposeProbe(trace: unknown): Promise<void> {
  try {
    await fetch("/api/mail/compose-probe", {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify(withTurnContext({ ...(trace as object) })),
    });
  } catch {
    /* diagnostics must not surface */
  }
}

// --- signature for HR-mailbox drafts ------------------------------------------

export type SignatureStatus = "none" | "confirmed" | "skipped";

export interface SignatureState {
  status: SignatureStatus;
  /** HTML with images inlined as data: URIs, ready for the editor. */
  html: string | null;
  canDetect: boolean;
}

export interface SignatureCandidate {
  html: string;
  missingImages: number;
  support: number;
  replySupport: number;
  confidence: "high" | "medium" | "low";
}

export interface SignatureDetection {
  scanned: number;
  replies: number;
  candidates: SignatureCandidate[];
}

/** Thrown when this deployment has no store, so the pane hides the feature. */
export class SignatureUnavailable extends Error {}

async function signatureFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(`/api/signature${path}`, { ...init, headers: await authHeaders() });
  if (res.status === 401 || res.status === 403) throw new NotAuthenticatedYet(`signature auth failed (${res.status})`);
  if (res.status === 503) throw new SignatureUnavailable();
  return res;
}

export async function getSignatureState(): Promise<SignatureState> {
  const res = await signatureFetch("");
  if (!res.ok) throw new Error(`signature read failed: ${res.status}`);
  return (await res.json()) as SignatureState;
}

/** Scan the user's own sent mail. `null` means detection cannot run for this user
 *  (no delegated token), which the pane answers by offering to paste instead. */
export async function detectSignature(): Promise<SignatureDetection | null> {
  const res = await signatureFetch("/detect", { method: "POST" });
  if (res.status === 409) return null;
  if (!res.ok) throw new Error(`signature detect failed: ${res.status}`);
  return (await res.json()) as SignatureDetection;
}

export async function saveSignature(html: string, source: "detected" | "edited" | "manual"): Promise<{ ok: true; html: string } | { ok: false; detail: string }> {
  const res = await signatureFetch("", { method: "PUT", body: JSON.stringify({ html, source }) });
  if (res.ok) return { ok: true, html: ((await res.json()) as { html: string }).html };
  const parsed = (await res.json().catch(() => null)) as { error?: string; detail?: string } | null;
  if (res.status === 413) return { ok: false, detail: parsed?.detail ?? "The signature is too large." };
  if (parsed?.error === "empty_signature") return { ok: false, detail: "The signature is empty." };
  return { ok: false, detail: "Couldn't save the signature. Try again." };
}

export async function skipSignature(): Promise<void> {
  await signatureFetch("/skip", { method: "POST" });
}

export async function removeSignature(): Promise<void> {
  const res = await signatureFetch("", { method: "DELETE" });
  if (!res.ok) throw new Error(`signature remove failed: ${res.status}`);
}
